import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import useStore from '../store/useStore';
import { useCrudOperations } from '../hooks/useCrudOperations';
import '../styles/ApiKeyManagement.css';

const ApiKeyManagement = () => {
  const navigate = useNavigate();
  const currentCompany = useStore((state) => state.currentCompany);

  const {
    data: apiKeys,
    loading,
    error,
    loadData: loadApiKeys,
    remove: removeApiKey,
    clearError
  } = useCrudOperations({
    apiService: adminAPI.apiKeys || adminAPI,
    resourceName: 'API key'
  });

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  // Filter API keys by current company
  const displayedKeys = currentCompany
    ? apiKeys.filter(key => key.company_id === currentCompany.id)
    : apiKeys;

  useEffect(() => {
    loadApiKeys();
  }, [currentCompany, loadApiKeys]);


  const handleCreateKey = async (e) => {
    e.preventDefault();

    if (!currentCompany || !currentCompany.id) {
      alert('No company selected');
      return;
    }

    if (!newKeyName || newKeyName.length < 3) {
      alert('Key name must be at least 3 characters long');
      return;
    }

    try {
      const response = await adminAPI.createApiKey(newKeyName, currentCompany.id);
      if (response.success) {
        setCreatedKey(response.data);
        setNewKeyName('');
        setShowCreateForm(false);
        await loadApiKeys();
      }
    } catch (err) {
      console.error('Failed to create API key:', err);
    }
  };

  const handleDeleteKey = async (id, keyName) => {
    await removeApiKey(
      id,
      `Are you sure you want to delete the API key "${keyName}"?\n\nThis action cannot be undone and will break any applications using this key.`,
      'API key deleted successfully'
    );
  };

  const handleToggleKey = async (id) => {
    try {
      const response = await adminAPI.toggleApiKey(id);
      if (response.success) {
        await loadApiKeys();
      }
    } catch (err) {
      console.error('Failed to toggle API key:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Handle modal close only if click starts and ends on overlay
  const handleModalOverlayMouseDown = (e) => {
    if (e.target.className === 'modal-overlay') {
      e.target.dataset.mousedownOnOverlay = 'true';
    }
  };

  const handleCreatedKeyModalClick = (e) => {
    if (e.target.className === 'modal-overlay' && e.target.dataset.mousedownOnOverlay === 'true') {
      setCreatedKey(null);
    }
    delete e.target.dataset.mousedownOnOverlay;
  };

  const handleCreateFormModalClick = (e) => {
    if (e.target.className === 'modal-overlay' && e.target.dataset.mousedownOnOverlay === 'true') {
      setShowCreateForm(false);
    }
    delete e.target.dataset.mousedownOnOverlay;
  };

  if (loading) {
    return (
      <div className="api-key-management">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="api-key-management">
      <div className="page-header">
        <div>
          <h2>API Key Management</h2>
          {currentCompany && (
            <p style={{ color: '#b0b0b0', fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Showing API keys for: <strong style={{ color: '#4a9eff' }}>{currentCompany.name}</strong>
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/settings')} className="btn-secondary">
            Back to Settings
          </button>
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(true)}
            disabled={!currentCompany}
          >
            + Create New API Key
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={clearError} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Created Key Modal */}
      {createdKey && (
        <div className="modal-overlay" onMouseDown={handleModalOverlayMouseDown} onClick={handleCreatedKeyModalClick}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>API Key Created Successfully!</h3>
            <p style={{ color: '#ffc107', marginTop: '1rem' }}>
              ⚠️ Copy this key now - you won't be able to see it again!
            </p>
            <div className="api-key-display">
              <code>{createdKey.apiKey}</code>
              <button
                onClick={() => copyToClipboard(createdKey.apiKey)}
                className="btn-primary"
              >
                Copy
              </button>
            </div>
            <div style={{ marginTop: '1.5rem' }}>
              <p><strong>Key Name:</strong> {createdKey.key_name}</p>
              <p><strong>Created:</strong> {formatDate(createdKey.created_at)}</p>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              className="btn-primary"
              style={{ marginTop: '1.5rem', width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="modal-overlay" onMouseDown={handleModalOverlayMouseDown} onClick={handleCreateFormModalClick}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New API Key</h3>
            <form onSubmit={handleCreateKey}>
              <div className="form-group">
                <label>Key Name *</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., C# Data Collector"
                  required
                  minLength={3}
                />
                <small>A descriptive name to identify this API key</small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create API Key
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* API Keys Table */}
      <div className="api-keys-table-container">
        {displayedKeys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#b0b0b0' }}>
            <p>No API keys found. Create one to get started.</p>
          </div>
        ) : (
          <table className="api-keys-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Prefix</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedKeys.map((key) => (
                <tr key={key.id}>
                  <td>
                    <strong>{key.key_name}</strong>
                  </td>
                  <td>
                    <span style={{ color: '#4a9eff' }}>{key.company_name}</span>
                  </td>
                  <td>
                    <code>{key.key_prefix}***</code>
                  </td>
                  <td>
                    <span className={`status-badge ${key.is_active ? 'active' : 'inactive'}`}>
                      {key.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(key.created_at)}</td>
                  <td>{formatDate(key.last_used_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleToggleKey(key.id)}
                        className="btn-toggle"
                        title={key.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {key.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.id, key.key_name)}
                        className="btn-delete"
                        title="Delete API key"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="info-section" style={{ marginTop: '2rem', padding: '1rem', background: '#2d2d2d', borderRadius: '8px' }}>
        <h4 style={{ marginBottom: '0.5rem' }}>About API Keys</h4>
        <ul style={{ marginLeft: '1.5rem', color: '#b0b0b0', lineHeight: '1.6' }}>
          <li>API keys are <strong>company-specific</strong> - each key grants access to all metrics and dashboards for that company</li>
          <li>Use API keys to authenticate external applications</li>
          <li>Each key is hashed and stored securely - you can only see it once when created</li>
          <li>Inactive keys will be rejected by the API</li>
          <li>Delete keys that are no longer in use</li>
          <li>Switch companies using the company selector to manage API keys for different companies</li>
        </ul>
      </div>
    </div>
  );
};

export default ApiKeyManagement;
